/**
 * Identity Providers Management Page
 *
 * Allows platform operators to register and manage identity providers (IdPs)
 * for federated authentication across the Aaim platform.
 *
 * Uses @aaimplatform/ui components and openapi-fetch typed client.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Select, Badge } from '@aaimplatform/ui';
import { createIdentityProviderClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';
import { CreateIdpModal } from '../components/CreateIdpModal';
import { formatApiError } from '../lib/api-error';

type IdentityProvider = components['schemas']['IdentityProvider'];
type IdentityProviderType = components['schemas']['IdentityProviderType'];
type IdentityProviderStatus = components['schemas']['IdentityProviderStatus'];

const PROVIDER_TYPE_LABELS: Record<string, string> = {
  oidc: 'OIDC',
  saml: 'SAML 2.0',
  fi_delegated: 'FI-Delegated',
  oauth2: 'OAuth 2.0',
  api_key: 'API Key',
  credential: 'Aaim Credential',
  mx_auth: 'MX Auth (Quiltt)',
};


export function IdentityProvidersPage() {
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<IdentityProviderType | ''>('');
  const [filterStatus, setFilterStatus] = useState<IdentityProviderStatus | ''>('');

  const client = createIdentityProviderClient();

  useEffect(() => {
    fetchProviders();
  }, [filterType, filterStatus]);

  async function fetchProviders() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: apiError, response } = await client.GET('/api/v1/identity-providers', {
        params: {
          query: {
            ...(filterType && { providerType: filterType }),
            ...(filterStatus && { status: filterStatus }),
          },
        },
      });

      if (apiError) {
        setError(formatApiError(apiError, 'Failed to load providers', response?.status));
        return;
      }

      if (data && 'success' in data && data.success && 'data' in data) {
        setProviders(data.data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }

  const filteredProviders = providers.filter((provider) => {
    if (filterType && provider.providerType !== filterType) return false;
    if (filterStatus && provider.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-secondary">Loading identity providers...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <p className="text-secondary text-sm">
          Manage federated authentication providers for the Aaim platform.
          Register Google Workspace, CU*Answers, Jack Henry, Fiserv, and custom OIDC/SAML providers.
        </p>
      </div>

      {/* Actions and Filters */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          {/* Filter by Type */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-xs text-tertiary whitespace-nowrap">Type:</label>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as IdentityProviderType | '')}
              className="w-full sm:w-40"
            >
              <option value="">All Types</option>
              <option value="oidc">OIDC</option>
              <option value="saml">SAML</option>
              <option value="oauth2">OAuth2</option>
              <option value="fi_delegated">FI-Delegated</option>
              <option value="api_key">API Key</option>
              <option value="mx_auth">MX Auth (Quiltt)</option>
            </Select>
          </div>

          {/* Filter by Status */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-xs text-tertiary whitespace-nowrap">Status:</label>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as IdentityProviderStatus | '')}
              className="w-full sm:w-48"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="pending_configuration">Pending Configuration</option>
              <option value="decommissioned">Decommissioned</option>
            </Select>
          </div>

          <div className="text-sm text-tertiary">
            {filteredProviders.length} provider{filteredProviders.length !== 1 ? 's' : ''}
          </div>
        </div>

        <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
          Register Provider
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Alert type="error" title="Error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Provider List */}
      {filteredProviders.length === 0 ? (
        <Card>
          <div className="p-8 text-center">
            <div className="text-secondary mb-4">
              {providers.length === 0
                ? 'No identity providers registered'
                : 'No providers match the selected filters'}
            </div>
            <p className="text-sm text-tertiary mb-6">
              {providers.length === 0
                ? 'Register your first identity provider to enable federated authentication for credit unions, core processors, or internal staff.'
                : 'Try adjusting your filters to see more providers.'}
            </p>
            {providers.length === 0 && (
              <Button variant="secondary" onClick={() => setCreateModalOpen(true)}>
                Register Provider
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredProviders.map((provider) => (
            <Card key={provider.id} className="hover:shadow-md transition-shadow">
              <Link to={`/identity-providers/${provider.id}`} className="block p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-sans text-lg font-medium">
                        {provider.name}
                      </h3>
                      <Badge
                        variant={provider.status === 'active' ? 'success' : provider.status === 'decommissioned' ? 'error' : provider.status === 'suspended' ? 'warning' : 'neutral'}
                        size="sm"
                      >
                        {provider.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-secondary">
                      <span>
                        Type: {PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType}
                      </span>
                      <span className="text-tertiary">|</span>
                      <span>Family: {provider.providerFamily}</span>
                      {provider.requireMfa && (
                        <>
                          <span className="text-tertiary">|</span>
                          <span className="text-accent font-medium">MFA Required</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-tertiary">
                    Updated {provider.updatedAt ? new Date(provider.updatedAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </Link>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateIdpModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(newProvider) => {
          setProviders([...providers, newProvider]);
          setCreateModalOpen(false);
        }}
      />
    </div>
  );
}
